import React from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { ContextPropagationPlanType } from '@shared/services/apiService/apiService';
import { HandoffRuleEditorValue } from '@features/Admin/Agent/handoffRules/domain/contracts';
import { VariableExtractionPlanEditor } from '@shared/components/VariableExtractionPlanEditor/VariableExtractionPlanEditor';
import { MessageGroupEditor } from '@shared/components/MessageGroupEditor/MessageGroupEditor';
import { useTranslation } from 'react-i18next';

type FocusedSection = 'route' | 'destinationContext' | 'variableExtraction' | 'toolMessages';

type HandoffDrawerProps = {
  open: boolean;
  focusedSection: FocusedSection;
  onFocusedSectionChange: (section: FocusedSection) => void;
  onClose: () => void;
  rule: HandoffRuleEditorValue | null;
  isDisabled: boolean;
  agentOptions: { id: string; label: string }[];
  onUpdateRule: (
    ruleId: string,
    updater: (rule: HandoffRuleEditorValue) => HandoffRuleEditorValue,
  ) => void;
  onDeleteRule: (ruleId: string) => void;
  onSaveChanges: () => Promise<void> | void;
  onCancel: () => void;
};

export const HandoffDrawer: React.FC<HandoffDrawerProps> = ({
  open,
  focusedSection,
  onFocusedSectionChange,
  onClose,
  rule,
  isDisabled,
  agentOptions,
  onUpdateRule,
  onDeleteRule,
  onSaveChanges,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDescriptionOpen, setIsDescriptionOpen] = React.useState(false);
  React.useEffect(() => {
    setIsDescriptionOpen(Boolean(rule?.description));
  }, [rule?.id, rule?.description]);
  if (!rule) return null;
  const withoutStepNumber = (label: string) => label.replace(/^\s*\d+\s*/, '').trim();
  const sectionTabs: Array<{ value: FocusedSection; label: string }> = [
    { value: 'route', label: withoutStepNumber(t('campaignPage.squads.handoffTool.tabs.route')) },
    {
      value: 'destinationContext',
      label: withoutStepNumber(t('campaignPage.squads.handoffTool.tabs.context')),
    },
    {
      value: 'variableExtraction',
      label: withoutStepNumber(t('campaignPage.squads.handoffTool.tabs.extraction')),
    },
    {
      value: 'toolMessages',
      label: withoutStepNumber(t('campaignPage.squads.handoffTool.tabs.messages')),
    },
  ];
  const getAgentLabel = (agentId: string, fallback: string) =>
    agentOptions.find((agent) => agent.id === agentId)?.label || fallback;
  const getAutoRuleName = (fromAgentId: string, toAgentId: string) => {
    if (!fromAgentId || !toAgentId) return '';
    return `${getAgentLabel(fromAgentId, 'From')} -> ${getAgentLabel(toAgentId, 'To')}`;
  };
  const handleDirectionChange = (key: 'fromAgentId' | 'toAgentId', value: string) => {
    onUpdateRule(rule.id, (prev) => {
      const nextFrom = key === 'fromAgentId' ? value : prev.fromAgentId;
      const nextTo = key === 'toAgentId' ? value : prev.toAgentId;
      const previousAutoName = getAutoRuleName(prev.fromAgentId, prev.toAgentId);
      const nextAutoName = getAutoRuleName(nextFrom, nextTo);
      const shouldAutoSyncName = !prev.name || prev.name === previousAutoName;
      return {
        ...prev,
        fromAgentId: nextFrom,
        toAgentId: nextTo,
        name: shouldAutoSyncName ? nextAutoName : prev.name,
      };
    });
  };
  const fieldStackSx = {
    py: 0.25,
  };
  const getTabLabel = (label: string) => <span style={{ whiteSpace: 'nowrap' }}>{label}</span>;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 760, md: 900 },
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        },
      }}>
      <Stack spacing={0} sx={{ height: '100%' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: -0.2 }}>
            {t('campaignPage.squads.handoffTool.title')}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Tabs
            value={focusedSection}
            onChange={(_, nextValue) => onFocusedSectionChange(nextValue)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              '& .MuiTabs-indicator': {
                height: 2,
                borderRadius: 999,
                backgroundColor: 'primary.main',
              },
              '& .MuiTab-root': {
                minHeight: 40,
                px: 1,
                py: 0.75,
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: 1.2,
                color: 'text.secondary',
                transition: 'color 120ms ease',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              },
            }}>
            {sectionTabs.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={getTabLabel(tab.label)} />
            ))}
          </Tabs>
        </Box>
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 2,
            overflowY: 'auto',
            flex: 1,
            // Floating labels can sit slightly above the input; keep breathing room at
            // the top of the scroll container so first-row labels never get clipped.
            '& .MuiFormControl-root': { overflow: 'visible' },
          }}>
          {focusedSection === 'route' && (
            <Stack spacing={2}>
              <Stack spacing={1.5} sx={fieldStackSx}>
                <Typography variant="subtitle2">
                  {t('campaignPage.squads.handoffTool.route.directionTitle')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <CustomTextField
                    select
                    fullWidth
                    size="small"
                    label={t('campaignPage.squads.handoffTool.route.fromAgent')}
                    value={rule.fromAgentId}
                    disabled={isDisabled}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleDirectionChange('fromAgentId', event.target.value)
                    }>
                    {agentOptions.map((agent) => (
                      <MenuItem key={agent.id} value={agent.id}>
                        {agent.label}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                  <CustomTextField
                    select
                    fullWidth
                    size="small"
                    label={t('campaignPage.squads.handoffTool.route.toAgent')}
                    value={rule.toAgentId}
                    disabled={isDisabled}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleDirectionChange('toAgentId', event.target.value)
                    }>
                    {agentOptions
                      .filter((agent) => agent.id !== rule.fromAgentId)
                      .map((agent) => (
                        <MenuItem key={agent.id} value={agent.id}>
                          {agent.label}
                        </MenuItem>
                      ))}
                  </CustomTextField>
                </Stack>
              </Stack>
              <CustomTextField
                label={t('campaignPage.squads.handoffTool.route.ruleName')}
                fullWidth
                size="small"
                value={rule.name}
                placeholder={t('campaignPage.squads.handoffTool.route.ruleNamePlaceholder')}
                disabled={isDisabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onUpdateRule(rule.id, (prev) => ({ ...prev, name: event.target.value }))
                }
              />
              {!isDescriptionOpen && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  sx={{
                    alignSelf: 'flex-start',
                    px: 0.25,
                    py: 0.25,
                    color: 'text.secondary',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: 1,
                    minHeight: 28,
                    '&:hover': { backgroundColor: 'transparent', color: 'primary.main' },
                  }}
                  onClick={() => setIsDescriptionOpen(true)}>
                  Add description
                </Button>
              )}
              <Collapse in={isDescriptionOpen}>
                <CustomTextField
                  label={t('campaignPage.squads.handoffTool.route.description')}
                  fullWidth
                  size="small"
                  value={rule.description}
                  placeholder={t('campaignPage.squads.handoffTool.route.descriptionPlaceholder')}
                  disabled={isDisabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onUpdateRule(rule.id, (prev) => ({ ...prev, description: event.target.value }))
                  }
                />
              </Collapse>
            </Stack>
          )}

          {focusedSection === 'destinationContext' && (
            <Stack spacing={2}>
              <CustomTextField
                select
                fullWidth
                size="small"
                label="Context plan"
                value={rule.contextPropagationPlan?.type || 'all'}
                InputLabelProps={{ shrink: true }}
                disabled={isDisabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onUpdateRule(rule.id, (prev) => ({
                    ...prev,
                    contextPropagationPlan: {
                      type: event.target.value as ContextPropagationPlanType,
                      ...(event.target.value === 'lastNMessages' ? { maxMessages: 5 } : {}),
                    },
                  }))
                }>
                <MenuItem value="all">{t('campaignPage.squads.handoffTool.context.all')}</MenuItem>
                <MenuItem value="lastNMessages">
                  {t('campaignPage.squads.handoffTool.context.lastNMessages')}
                </MenuItem>
              </CustomTextField>
              {rule.contextPropagationPlan?.type === 'lastNMessages' && (
                <CustomTextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('campaignPage.squads.handoffTool.context.maxMessages')}
                  value={rule.contextPropagationPlan.maxMessages ?? 5}
                  disabled={isDisabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onUpdateRule(rule.id, (prev) => ({
                      ...prev,
                      contextPropagationPlan: {
                        type: 'lastNMessages',
                        maxMessages: Number(event.target.value),
                      },
                    }))
                  }
                />
              )}
              <CustomTextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label={t('campaignPage.squads.handoffTool.context.summaryLabel')}
                placeholder={t('campaignPage.squads.handoffTool.context.summaryPlaceholder')}
                value={rule.contextSummaryPlan?.summary ?? ''}
                disabled={isDisabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onUpdateRule(rule.id, (prev) => ({
                    ...prev,
                    contextSummaryPlan: { summary: event.target.value },
                  }))
                }
              />
            </Stack>
          )}

          {focusedSection === 'variableExtraction' && (
            <Box sx={{ pb: 1 }}>
              <VariableExtractionPlanEditor
                value={rule.variableExtractionPlan}
                disabled={isDisabled}
                labels={{
                  helperText: t('campaignPage.squads.handoffTool.extraction.helper'),
                  emptyStateText: t('campaignPage.squads.handoffTool.extraction.emptyState'),
                  variableNameHeader: t('campaignPage.squads.handoffTool.extraction.variableName'),
                  typeHeader: t('campaignPage.squads.handoffTool.extraction.type'),
                  requiredHeader: t('campaignPage.squads.handoffTool.extraction.required'),
                  actionsHeader: t('campaignPage.squads.handoffTool.extraction.actions'),
                  variableNamePlaceholder: t(
                    'campaignPage.squads.handoffTool.extraction.variableNamePlaceholder',
                  ),
                  editFieldDetailsAriaLabel: t(
                    'campaignPage.squads.handoffTool.extraction.editFieldDetails',
                  ),
                  descriptionLabel: t('campaignPage.squads.handoffTool.route.description'),
                  descriptionPlaceholder: t(
                    'campaignPage.squads.handoffTool.extraction.descriptionPlaceholder',
                  ),
                  enumValuesLabel: t('campaignPage.squads.handoffTool.extraction.enumValues'),
                  addPropertyLabel: t('campaignPage.squads.handoffTool.extraction.addProperty'),
                }}
                onChange={(variableExtractionPlan) =>
                  onUpdateRule(rule.id, (prev) => ({ ...prev, variableExtractionPlan }))
                }
              />
            </Box>
          )}

          {focusedSection === 'toolMessages' && (
            <Stack spacing={2}>
              <Stack spacing={1.5}>
                <CustomTextField
                  select
                  fullWidth
                  size="small"
                  label={t('campaignPage.squads.handoffTool.messages.startAgent')}
                  value={rule.sessionBoundaryPlan?.startAgentId ?? ''}
                  disabled={isDisabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onUpdateRule(rule.id, (prev) => ({
                      ...prev,
                      sessionBoundaryPlan: {
                        startAgentId: event.target.value || undefined,
                        endingAgentIds: prev.sessionBoundaryPlan?.endingAgentIds ?? [],
                        allowSessionWithoutEndingAgent: true,
                      },
                    }))
                  }>
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {agentOptions.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.label}
                    </MenuItem>
                  ))}
                </CustomTextField>

                <CustomTextField
                  select
                  fullWidth
                  size="small"
                  label={t('campaignPage.squads.handoffTool.messages.endingAgents')}
                  value={rule.sessionBoundaryPlan?.endingAgentIds ?? []}
                  disabled={isDisabled}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected: unknown) => (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(selected as string[]).map((agentId) => (
                          <Chip
                            key={agentId}
                            size="small"
                            label={
                              agentOptions.find((option) => option.id === agentId)?.label || agentId
                            }
                          />
                        ))}
                      </Stack>
                    ),
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onUpdateRule(rule.id, (prev) => ({
                      ...prev,
                      sessionBoundaryPlan: {
                        startAgentId: prev.sessionBoundaryPlan?.startAgentId,
                        endingAgentIds: event.target.value as unknown as string[],
                        allowSessionWithoutEndingAgent: true,
                      },
                    }))
                  }>
                  {agentOptions.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.label}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Stack>
              <MessageGroupEditor
                groups={['start', 'delayed', 'complete', 'failed']}
                value={rule.messages}
                disabled={isDisabled}
                labels={{
                  helperText: t('campaignPage.squads.handoffTool.messages.groupedHelper'),
                  addMessageLabel: t('campaignPage.squads.handoffTool.messages.add'),
                  emptyGroupText: t('campaignPage.squads.handoffTool.messages.emptyState'),
                  messagePlaceholder: t('campaignPage.squads.handoffTool.messages.contentText'),
                  groupLabels: {
                    start: t('campaignPage.squads.handoffTool.messages.requestStart'),
                    delayed: t('campaignPage.squads.handoffTool.messages.requestDelayed'),
                    complete: t('campaignPage.squads.handoffTool.messages.requestComplete'),
                    failed: t('campaignPage.squads.handoffTool.messages.requestFailed'),
                  },
                  deleteMessageAriaLabel: (group, index) =>
                    t('campaignPage.squads.handoffTool.messages.deleteMessage', {
                      index,
                    }) + ` (${group})`,
                }}
                onChange={(messages) => onUpdateRule(rule.id, (prev) => ({ ...prev, messages }))}
              />
            </Stack>
          )}
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            position: 'sticky',
            bottom: 0,
            px: 2,
            py: 1.5,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.98),
          }}>
          <Button
            size="small"
            color="error"
            variant="text"
            disabled={isDisabled}
            onClick={() => onDeleteRule(rule.id)}>
            {t('common.delete')}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button size="small" variant="contained" disabled={isDisabled} onClick={onSaveChanges}>
              {t('campaignPage.squads.editor.actions.done')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};
